// ─────────────────────────────────────────────────────────────────────────────
// DETECTOR STATE — data access layer
//
// Reads and writes to two tables created in Phase 6:
//   - detector_state      : user's relationship to each pattern
//                           (active / acknowledged / working_on_it / dismissed)
//   - detector_snapshots  : append-only daily time series of detector readings
//
// Talks to Supabase via the project's authFetch helper (NOT @supabase/supabase-js).
// Matches the pattern used elsewhere in src/lib.
//
// All functions take a `userId` explicitly — RLS will reject unauthorized rows
// regardless, but passing userId in keeps the queries explicit and lets us
// fail early if the caller forgot to thread it through.
//
// `account_id` is nullable in both tables:
//   - per-account row : account_id = '<uuid>'
//   - user-wide row   : account_id IS NULL
// We default to per-account scoping but allow opting into user-wide via
// `accountId === null`. The DB has UNIQUE constraints that prevent duplicate
// rows at the same scope (see migration in Phase 6 schema).
// ─────────────────────────────────────────────────────────────────────────────

import { SUPABASE_URL, authFetch } from "./supabase";

const TABLE_STATE     = "detector_state";
const TABLE_SNAPSHOTS = "detector_snapshots";

// PostgREST filter for account_id: distinguishes "specific account" from "user-wide"
// (account_id IS NULL). Returns the right query fragment for either.
function accountIdFilter(accountId) {
  if (accountId === null || accountId === undefined) return "account_id=is.null";
  return `account_id=eq.${encodeURIComponent(accountId)}`;
}

// Strip undefined keys so PostgREST doesn't complain about unknown columns.
function clean(obj) {
  const out = {};
  for (const k of Object.keys(obj)) {
    if (obj[k] !== undefined) out[k] = obj[k];
  }
  return out;
}

// ─── STATE: loading ─────────────────────────────────────────────────────────

// Load every detector_state row visible to this user, both per-account and
// user-wide. Caller merges them per-detector with "per-account wins over
// user-wide" semantics. Returns an array of raw rows (no transformation).
//
// Why both at once: we don't want a flash of "active" state on tab load
// while a second query resolves. One round-trip gets everything.
export async function loadDetectorStates({ userId }) {
  if (!userId) throw new Error("loadDetectorStates: userId required");
  const url = `${SUPABASE_URL}/rest/v1/${TABLE_STATE}`
    + `?user_id=eq.${encodeURIComponent(userId)}`
    + `&select=*`;
  const res = await authFetch(url);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`loadDetectorStates ${res.status}: ${body}`);
  }
  return res.json();
}

// Resolve the effective state for a single (account, detector). Per-account
// row wins if present; otherwise user-wide row; otherwise the default 'active'
// stub. Callers usually iterate this for each detector they render.
export function resolveDetectorState(allRows, { accountId, detectorId }) {
  const perAccount = allRows.find(
    r => r.account_id === accountId && r.detector_id === detectorId
  );
  if (perAccount) return perAccount;
  const userWide = allRows.find(
    r => r.account_id === null && r.detector_id === detectorId
  );
  if (userWide) return userWide;
  return {
    detector_id: detectorId,
    account_id: accountId,
    status: "active",
    // Other columns intentionally undefined — caller knows this is a stub
  };
}

// ─── STATE: writing ─────────────────────────────────────────────────────────

// UPSERT a detector_state row. Status transitions automatically stamp the
// matching *_at timestamp (acknowledged_at, working_on_at, dismissed_at).
//
// `accountId === null` means "this state applies to all my accounts" and will
// land on the user-wide row (the partial-NULL UNIQUE index makes that legal
// alongside any per-account rows for the same detector).
//
// Optional working-on-it fields: target_metric, target_value, target_baseline,
// target_date. When status !== 'working_on_it' these are ignored by callers
// but still upserted so we don't accidentally erase a previous target if the
// user toggles status back and forth.
export async function setDetectorStatus({
  userId,
  accountId,            // uuid | null
  detectorId,
  status,               // 'active' | 'acknowledged' | 'working_on_it' | 'dismissed'
  targetMetric,
  targetValue,
  targetBaseline,
  targetDate,           // 'YYYY-MM-DD'
  notes,
}) {
  if (!userId) throw new Error("setDetectorStatus: userId required");
  if (!detectorId) throw new Error("setDetectorStatus: detectorId required");
  if (!status) throw new Error("setDetectorStatus: status required");

  const now = new Date().toISOString();
  const row = clean({
    user_id:         userId,
    account_id:      accountId ?? null,
    detector_id:     detectorId,
    status,
    target_metric:   targetMetric,
    target_value:    targetValue,
    target_baseline: targetBaseline,
    target_date:     targetDate,
    notes,
    acknowledged_at: status === "acknowledged"   ? now : undefined,
    working_on_at:   status === "working_on_it"  ? now : undefined,
    dismissed_at:    status === "dismissed"      ? now : undefined,
  });

  // PostgREST UPSERT needs the on_conflict parameter to name the columns of
  // the unique constraint or index to merge against. We have TWO indexes:
  //
  //   (1) UNIQUE (user_id, account_id, detector_id)          -- standard
  //   (2) UNIQUE (user_id, detector_id) WHERE account_id IS NULL -- partial
  //
  // Postgres treats NULLs as distinct in standard UNIQUE constraints, so (1)
  // can never match a row where account_id IS NULL. We must use (2)'s columns
  // when sending account_id=null, and (1)'s columns otherwise.
  const isUserWide = (row.account_id === null || row.account_id === undefined);
  const conflictCols = isUserWide
    ? "user_id,detector_id"
    : "user_id,account_id,detector_id";

  const url = `${SUPABASE_URL}/rest/v1/${TABLE_STATE}`
    + `?on_conflict=${conflictCols}`;
  const res = await authFetch(url, {
    method: "POST",
    headers: {
      Prefer: "resolution=merge-duplicates,return=representation",
    },
    body: JSON.stringify(row),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`setDetectorStatus ${res.status}: ${body}`);
  }
  const out = await res.json();
  return Array.isArray(out) ? out[0] : out;
}

// Convenience: clear status back to default. We don't delete the row (the
// audit timestamps are useful history) — we just flip status to 'active'.
export async function clearDetectorStatus({ userId, accountId, detectorId }) {
  return setDetectorStatus({ userId, accountId, detectorId, status: "active" });
}

// ─── SNAPSHOTS: writing ─────────────────────────────────────────────────────

// Today in 'YYYY-MM-DD'. Uses local time intentionally — the user's "today"
// matters more than UTC's "today" for behavioral pattern dates.
function todayLocal() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Map a single detector result → 0..N snapshot rows. Each detector publishes
// its own primary metrics; we record those, not the full details blob.
//
// This is the ONE place where detector → snapshot mapping is defined. Adding
// a new detector requires adding a case here for it to land in history. If a
// detector returns 'insufficient' confidence we still record n + confidence
// (with metric_value = 0) so the sparkline can show "we had insufficient
// data on this date" honestly.
function snapshotsFromDetector({ userId, accountId, detector, date }) {
  if (!detector) return [];
  const base = {
    user_id:      userId,
    account_id:   accountId ?? null,
    detector_id:  detector.id,
    snapshot_date: date,
    n:            detector.n ?? null,
    confidence:   detector.confidence ?? null,
  };
  const rows = [];
  const push = (metric_name, metric_value) => {
    if (metric_value == null || isNaN(metric_value)) return;
    rows.push({ ...base, metric_name, metric_value: Number(metric_value) });
  };

  // Always record the headline cost (matches what the card displays).
  push("estimated_cost", detector.estimated_cost ?? 0);

  // Detector-specific metrics. Mirrors the details shape each detector returns.
  switch (detector.id) {
    case "revenge": {
      const d = detector.details || {};
      push("revenge_winrate",  d.revenge_winrate);
      push("baseline_winrate", d.baseline_winrate);
      push("revenge_total_pnl", d.revenge_total_pnl);
      break;
    }
    case "size_tilt":
    case "win_streak_euphoria": {
      const d = detector.details || {};
      push("tilt_ratio",         d.tilt_ratio);
      push("extra_exposure_pnl", d.extra_exposure_pnl);
      break;
    }
    case "hour_cliff": {
      const d = detector.details || {};
      push("overall_winrate", d.overall_winrate);
      push("worst_slot_net",  d.worst_slot?.total_net);
      break;
    }
    case "day_of_week": {
      const d = detector.details || {};
      push("best_day_net",  d.best?.total_net);
      push("worst_day_net", d.worst?.total_net);
      break;
    }
    case "time_in_trade": {
      const d = detector.details || {};
      push("avg_win_seconds",  d.avg_win_seconds);
      push("avg_loss_seconds", d.avg_loss_seconds);
      push("ratio",            d.ratio);
      break;
    }
    case "overtrading": {
      const d = detector.details || {};
      push("correlation", d.correlation);
      push("light_avg",   d.light_days?.avg_pnl);
      push("heavy_avg",   d.heavy_days?.avg_pnl);
      break;
    }
    case "size_adherence": {
      const d = detector.details || {};
      push("violation_count", d.violation_count);
      push("violation_pnl",   d.violation_pnl);
      break;
    }
    case "drawdown_proximity": {
      const d = detector.details || {};
      push("worst_day_pct", d.worst_day_pct);
      break;
    }
    case "stop_adherence": {
      const d = detector.details || {};
      push("adherence_rate",   d.adherence_rate);
      push("overshoot_dollars", d.total_overshoot_dollars);
      break;
    }
    case "stop_widening": {
      const d = detector.details || {};
      push("widening_ratio",   d.widening_ratio);
      push("widened_net_pnl",  d.widened_net_pnl);
      break;
    }
    case "r_multiples": {
      const d = detector.details || {};
      push("avg_win_r",     d.avg_win_r);
      push("avg_loss_r",    d.avg_loss_r);
      push("expectancy_r",  d.expectancy_r);
      break;
    }
    case "rule_violations": {
      const d = detector.details || {};
      push("total_violations", d.total_violations);
      push("total_loss",       d.total_loss);
      break;
    }

    // ── Context detectors (Phase 8 Pass B) ───────────────────────────────
    case "news_event": {
      const d = detector.details || {};
      push("news_wr",      d.news_wr);
      push("normal_wr",    d.normal_wr);
      push("wr_gap_pp",    d.wr_gap_pp);
      push("news_avg_net", d.news_avg_net);
      break;
    }
    case "manual_exit": {
      const d = detector.details || {};
      push("manual_avg_net",  d.manual_avg_net);
      push("planned_avg_net", d.planned_avg_net);
      push("edge_diff",       d.edge_diff);
      push("override_rate",   d.override_rate);
      break;
    }
    case "market_regime": {
      const d = detector.details || {};
      push("edge_diff",    d.edge_diff);
      push("best_avg_net", d.best?.avg_net);
      push("worst_avg_net", d.worst?.avg_net);
      break;
    }
    case "setup_edge": {
      const d = detector.details || {};
      push("n_leaking",    d.leaking_setups?.length ?? 0);
      push("best_avg_net", d.best?.avg_net);
      push("worst_avg_net", d.worst?.avg_net);
      break;
    }

    // ── Psychology detectors (Phase 8 Pass C) ────────────────────────────
    case "fear_spike": {
      const d = detector.details || {};
      push("fear_avg_net",   d.fear_avg_net);
      push("normal_avg_net", d.normal_avg_net);
      push("edge_diff",      d.edge_diff);
      push("wr_gap_pp",      d.wr_gap_pp);
      break;
    }
    case "impulsiveness": {
      const d = detector.details || {};
      push("impulse_avg_net", d.impulse_avg_net);
      push("calm_avg_net",    d.calm_avg_net);
      push("edge_diff",       d.edge_diff);
      push("wr_gap_pp",       d.wr_gap_pp);
      break;
    }
    case "low_focus": {
      const d = detector.details || {};
      push("low_avg_net",    d.low_avg_net);
      push("normal_avg_net", d.normal_avg_net);
      push("edge_diff",      d.edge_diff);
      break;
    }
    case "mental_state": {
      const d = detector.details || {};
      push("best_avg_net",  d.best?.avg_net);
      push("worst_avg_net", d.worst?.avg_net);
      break;
    }
    case "conviction_mismatch": {
      const d = detector.details || {};
      push("high_avg_net", d.high_conv?.avg_net);
      push("low_avg_net",  d.low_conv?.avg_net);
      push("edge_diff",    d.edge_diff);
      break;
    }

    default:
      // Unknown detector — only the estimated_cost row is written.
      break;
  }

  return rows;
}

// UPSERT today's snapshots for an entire detector run in one batch. Called
// from the Behavior / Risk Discipline tabs on mount. Idempotent: rerunning
// the same day overwrites with the latest reading via ON CONFLICT.
//
// `results` is the array from runAllDetectors(...).detectors or
// runRiskDetectors(...).detectors. We flatten it to N rows and POST once.
export async function writeSnapshots({ userId, accountId, results }) {
  if (!userId) throw new Error("writeSnapshots: userId required");
  if (!Array.isArray(results) || results.length === 0) return [];

  const date = todayLocal();
  const rows = results.flatMap(d =>
    snapshotsFromDetector({ userId, accountId, detector: d, date })
  );

  if (rows.length === 0) return [];

  // UPSERT in one POST. Conflict columns must match a unique constraint or
  // partial index. Like detector_state, we have two indexes — the standard
  // 5-column UNIQUE and a partial 4-column UNIQUE for the account_id=null
  // case. Send the right one based on the rows we're writing. (We write
  // all rows in a single POST with a single accountId, so the choice is
  // global to the batch.)
  const isUserWide = (accountId === null || accountId === undefined);
  const conflictCols = isUserWide
    ? "user_id,detector_id,snapshot_date,metric_name"
    : "user_id,account_id,detector_id,snapshot_date,metric_name";

  const url = `${SUPABASE_URL}/rest/v1/${TABLE_SNAPSHOTS}`
    + `?on_conflict=${conflictCols}`;
  const res = await authFetch(url, {
    method: "POST",
    headers: {
      Prefer: "resolution=merge-duplicates,return=minimal",
    },
    body: JSON.stringify(rows),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`writeSnapshots ${res.status}: ${body}`);
  }
  return rows; // return what we tried to write, for debug/observability
}

// ─── SNAPSHOTS: reading ─────────────────────────────────────────────────────

// Fetch sparkline data for one detector + metric. Returns rows ordered
// snapshot_date ASC so the caller can plot them left → right.
export async function loadSnapshots({
  userId,
  accountId,
  detectorId,
  metricName,
  sinceDays = 90,
}) {
  if (!userId) throw new Error("loadSnapshots: userId required");
  if (!detectorId) throw new Error("loadSnapshots: detectorId required");

  const since = new Date();
  since.setDate(since.getDate() - sinceDays);
  const sinceStr = since.toISOString().slice(0, 10);

  const params = [
    `user_id=eq.${encodeURIComponent(userId)}`,
    accountIdFilter(accountId),
    `detector_id=eq.${encodeURIComponent(detectorId)}`,
    metricName ? `metric_name=eq.${encodeURIComponent(metricName)}` : null,
    `snapshot_date=gte.${sinceStr}`,
    `select=snapshot_date,metric_name,metric_value,n,confidence`,
    `order=snapshot_date.asc`,
  ].filter(Boolean).join("&");

  const url = `${SUPABASE_URL}/rest/v1/${TABLE_SNAPSHOTS}?${params}`;
  const res = await authFetch(url);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`loadSnapshots ${res.status}: ${body}`);
  }
  return res.json();
}

// Convenience: latest N snapshots across ALL metrics for a detector. Useful
// when expanding a detail card to show multiple sparklines side-by-side.
export async function loadDetectorHistory({ userId, accountId, detectorId, sinceDays = 90 }) {
  return loadSnapshots({ userId, accountId, detectorId, metricName: null, sinceDays });
}

// ─── TARGET METRIC REGISTRY ─────────────────────────────────────────────────
//
// When a user clicks "Working on it" for a detector, we need to know:
//   1. Which metric they're targeting (one per detector)
//   2. Which direction "better" goes (lower or higher)
//   3. The current value of that metric (so we can show progress)
//   4. A sensible default target (so the form isn't empty)
//
// Centralizing this means a) the UI form is generic — it just reads from here,
// and b) adding a new detector means one entry here, no UI changes.
//
// extract(detector)         → current numeric value of the target metric
// defaultTarget(detector)   → suggested target value, usually "close half the gap"
// direction                 → 'down' (smaller = better) or 'up' (larger = better)
// unit                      → display unit shown next to the input
// label                     → human-readable name of the metric for the form
// formatter(value)          → presentation function for displaying values
// ─────────────────────────────────────────────────────────────────────────────

const pct = (v) => v == null ? "—" : `${(v * 100).toFixed(0)}%`;
const num = (v) => v == null ? "—" : Number(v).toFixed(2);

export const TARGET_DEFAULTS = {
  revenge: {
    metric:     "revenge_winrate_gap",
    label:      "Win-rate gap (baseline − revenge)",
    direction:  "down",
    unit:       "pp",
    extract:    (d) => {
      const dd = d.details || {};
      if (dd.baseline_winrate == null || dd.revenge_winrate == null) return null;
      return (dd.baseline_winrate - dd.revenge_winrate) * 100;  // in percentage points
    },
    defaultTarget: (current) => current != null ? Math.max(5, Math.round(current / 2)) : 10,
    formatter:  (v) => v == null ? "—" : `${Number(v).toFixed(0)}pp`,
  },

  size_tilt: {
    metric:     "tilt_ratio",
    label:      "Tilt ratio (size after losses ÷ baseline)",
    direction:  "down",
    unit:       "x",
    extract:    (d) => d.details?.tilt_ratio ?? null,
    defaultTarget: (current) => current != null ? Math.max(1.05, Number((1 + (current - 1) / 2).toFixed(2))) : 1.15,
    formatter:  (v) => v == null ? "—" : `${num(v)}x`,
  },

  win_streak_euphoria: {
    metric:     "tilt_ratio",
    label:      "Tilt ratio (size after wins ÷ baseline)",
    direction:  "down",
    unit:       "x",
    extract:    (d) => d.details?.tilt_ratio ?? null,
    defaultTarget: (current) => current != null ? Math.max(1.05, Number((1 + (current - 1) / 2).toFixed(2))) : 1.15,
    formatter:  (v) => v == null ? "—" : `${num(v)}x`,
  },

  hour_cliff: {
    metric:     "worst_slot_net",
    label:      "Net P&L of your worst time slot",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.worst_slot?.total_net ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  day_of_week: {
    metric:     "worst_day_net",
    label:      "Net P&L on your worst weekday",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.worst?.total_net ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  time_in_trade: {
    metric:     "loss_win_ratio",
    label:      "Loss-to-win hold-time ratio",
    direction:  "down",
    unit:       "x",
    extract:    (d) => d.details?.ratio ?? null,
    defaultTarget: (current) => current != null ? Math.max(1.0, Number(((1 + current) / 2).toFixed(2))) : 1.2,
    formatter:  (v) => v == null ? "—" : `${num(v)}x`,
  },

  overtrading: {
    metric:     "heavy_vs_light_gap",
    label:      "Light-day avg P&L − heavy-day avg P&L",
    direction:  "down",
    unit:       "$",
    extract:    (d) => {
      const dd = d.details || {};
      if (dd.light_days?.avg_pnl == null || dd.heavy_days?.avg_pnl == null) return null;
      return dd.light_days.avg_pnl - dd.heavy_days.avg_pnl;  // positive = heavy days underperform
    },
    defaultTarget: (current) => current != null ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  size_adherence: {
    metric:     "violation_count",
    label:      "Size-rule violation count",
    direction:  "down",
    unit:       "trades",
    extract:    (d) => d.details?.violation_count ?? null,
    defaultTarget: () => 0,
    formatter:  (v) => v == null ? "—" : String(Math.round(v)),
  },

  stop_adherence: {
    metric:     "adherence_rate",
    label:      "Stop adherence rate",
    direction:  "up",
    unit:       "%",
    extract:    (d) => d.details?.adherence_rate ?? null,
    defaultTarget: (current) => current != null ? Math.min(1, current + (1 - current) / 2) : 0.95,
    formatter:  (v) => pct(v),
  },

  stop_widening: {
    metric:     "widening_ratio",
    label:      "Widening ratio (streak 3+ ÷ baseline)",
    direction:  "down",
    unit:       "x",
    extract:    (d) => d.details?.widening_ratio ?? null,
    defaultTarget: (current) => current != null ? Math.max(1.0, Number((1 + (current - 1) / 2).toFixed(2))) : 1.10,
    formatter:  (v) => v == null ? "—" : `${num(v)}x`,
  },

  r_multiples: {
    metric:     "expectancy_r",
    label:      "Expectancy (in R)",
    direction:  "up",
    unit:       "R",
    extract:    (d) => d.details?.expectancy_r ?? null,
    defaultTarget: (current) => current != null ? Number(Math.max(0.1, current + 0.2).toFixed(2)) : 0.3,
    formatter:  (v) => v == null ? "—" : `${num(v)}R`,
  },

  drawdown_proximity: {
    metric:     "worst_day_pct",
    label:      "Worst-day % of daily DD limit",
    direction:  "down",
    unit:       "%",
    extract:    (d) => d.details?.worst_day_pct ?? null,
    defaultTarget: (current) => current != null ? Math.max(0.2, current / 2) : 0.5,
    formatter:  (v) => pct(v),
  },

  // ── Context detectors (Phase 8 Pass B) ─────────────────────────────────
  news_event: {
    metric:     "wr_gap_pp",
    label:      "Win-rate gap (normal − news) in percentage points",
    direction:  "down",
    unit:       "pp",
    extract:    (d) => d.details?.wr_gap_pp != null ? -d.details.wr_gap_pp : null,
    // wr_gap_pp is (news_wr - normal_wr)*100; negative means news underperforms.
    // We negate for display so "down" direction means "close the gap".
    defaultTarget: () => 0,
    formatter:  (v) => v == null ? "—" : `${Number(v).toFixed(0)}pp`,
  },

  manual_exit: {
    metric:     "edge_diff",
    label:      "Manual exit avg vs planned exit avg ($/trade)",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.edge_diff ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  market_regime: {
    metric:     "worst_avg_net",
    label:      "Avg net P&L in your worst regime",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.worst?.avg_net ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  setup_edge: {
    metric:     "n_leaking",
    label:      "Number of reliably net-negative setups",
    direction:  "down",
    unit:       "setups",
    extract:    (d) => d.details?.leaking_setups?.length ?? null,
    defaultTarget: () => 0,
    formatter:  (v) => v == null ? "—" : String(Math.round(v)),
  },

  // ── Psychology detectors (Phase 8 Pass C) ──────────────────────────────
  fear_spike: {
    metric:     "edge_diff",
    label:      "Fear trade avg vs baseline avg ($/trade)",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.edge_diff ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  impulsiveness: {
    metric:     "edge_diff",
    label:      "Impulsive trade avg vs calm avg ($/trade)",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.edge_diff ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  low_focus: {
    metric:     "edge_diff",
    label:      "Low-focus trade avg vs normal avg ($/trade)",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.edge_diff ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  mental_state: {
    metric:     "worst_avg_net",
    label:      "Avg net P&L in your worst mental state",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.worst?.avg_net ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },

  conviction_mismatch: {
    metric:     "edge_diff",
    label:      "High-conviction avg vs mid-conviction avg ($/trade)",
    direction:  "up",
    unit:       "$",
    extract:    (d) => d.details?.edge_diff ?? null,
    defaultTarget: (current) => current != null && current < 0 ? Number((current / 2).toFixed(2)) : 0,
    formatter:  (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`,
  },
};

// Convenience: get the target descriptor for a given detector, or null if
// not in the registry. UI can use this to decide whether to render the
// working-on-it form at all.
export function getTargetDescriptor(detectorId) {
  return TARGET_DEFAULTS[detectorId] || null;
}

// Compute "progress toward target" as a 0..1 ratio.
//   - direction 'down': starting at baseline, going down to target. Progress
//     is (baseline - current) / (baseline - target).
//   - direction 'up'  : starting at baseline, going up to target. Progress
//     is (current - baseline) / (target - baseline).
// Returns 0 if no progress yet; can exceed 1 if user beats the target.
export function computeProgress({ direction, baseline, current, target }) {
  if (baseline == null || current == null || target == null) return null;
  const span = direction === "down" ? baseline - target : target - baseline;
  if (span === 0) return current === target ? 1 : 0;
  const moved = direction === "down" ? baseline - current : current - baseline;
  return moved / span;
}
